import { countries } from "countries-list";
import { Group, Select } from "@mantine/core";
import { useController } from "react-hook-form";
import Flags from "country-flag-icons/react/3x2";

const yugoslavianCodes = ["RS", "BA", "HR", "MK", "ME", "SI"];

const allCountries = Object.entries(countries).map(([code, country]) => ({
  value: code,
  label: country.name,
}));

const yugoslavianCountries = allCountries.filter((country) =>
  yugoslavianCodes.includes(country.value)
);

const otherCountries = allCountries
  .filter(
    (country) =>
      !yugoslavianCodes.includes(country.value) && country.value !== "XK"
  )
  .sort((a, b) => a.label.localeCompare(b.label));

const groupedData = [
  { group: "Ex-Yugoslavian Countries", items: yugoslavianCountries },
  { group: "Other Countries", items: otherCountries },
];

export const selectableCountries = [...yugoslavianCountries, ...otherCountries];

const codeByLabel = new Map(
  selectableCountries.map((country) => [
    country.label.toLowerCase(),
    country.value,
  ])
);

/**
 * A country selection component that displays countries in a searchable dropdown.
 * Countries are grouped into "Ex-Yugoslavian Countries" and "Other Countries" categories.
 *
 * Values are ISO 3166-1 alpha-2 codes (e.g. "RS"). The field is registered via
 * useController, so `control` and `name` are required.
 *
 * Browser address autofill cannot drive Mantine's Select on its own: the visible
 * input only holds search text, and the value changes solely when an option is
 * submitted. Autofill support therefore relies on two extra mechanisms:
 * - a visually hidden native <select autocomplete="country"> synced with the
 *   field — browsers fill native selects reliably and match localized country
 *   names against the ISO code option values;
 * - an exact label match on search text, so a full country name typed or
 *   autofilled into the visible input commits the corresponding code.
 *
 * @component
 * @param {import("react-hook-form").UseControllerProps &
 *   Omit<import("@mantine/core").SelectProps, "value" | "defaultValue">} props
 * @returns {JSX.Element} A Select component with grouped country options
 *
 * @example
 * <CountrySelect
 *   control={control}
 *   name="additionalFields.country"
 *   label="Country"
 *   placeholder="Select a country"
 * />
 */
function CountrySelect({
  control,
  name,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  onSearchChange,
  ...props
}) {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController({ name, control, defaultValue, rules, shouldUnregister });

  const handleChange = (nextValue, option) => {
    fieldOnChange(nextValue);
    onChange?.(nextValue, option);
  };

  const handleSearchChange = (searchText) => {
    const code = codeByLabel.get(searchText.trim().toLowerCase());
    if (code && code !== value) {
      fieldOnChange(code);
    }
    onSearchChange?.(searchText);
  };

  return (
    <Select
      data={groupedData}
      searchable
      renderOption={renderOption}
      autoComplete="off"
      value={value ?? null}
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      error={fieldState.error?.message}
      inputContainer={(children) => (
        <div style={{ position: "relative" }}>
          {children}
          {/* Autofill target: opacity keeps it fillable, display:none would not */}
          <select
            tabIndex={-1}
            aria-hidden="true"
            autoComplete="country"
            value={value ?? ""}
            onChange={(event) => handleChange(event.target.value || null)}
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0,
              border: 0,
              pointerEvents: "none",
            }}
          >
            <option value="" />
            {selectableCountries.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {...field}
      {...props}
    />
  );
}

function renderOption({ option }) {
  const FlagComponent = Flags[option.value.toUpperCase()];

  return (
    <Group flex="1" gap="xs">
      {FlagComponent && <FlagComponent height="12px" />}
      {option.label}
    </Group>
  );
}

export default CountrySelect;
